import { useCallback, useLayoutEffect, useMemo, useRef, type RefObject } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type ApiKeyDialogData = {
  name: string;
  expires_at?: string | null;
  key?: string | null;
  secret_key?: string | null;
};

interface ApiKeySuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  apiKey: ApiKeyDialogData | null;
  note: string;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
  onCloseAutoFocus?: (event: Event) => void;
}

const ApiKeySuccessDialog = ({
  open,
  onOpenChange,
  title,
  apiKey,
  note,
  returnFocusRef,
  onCloseAutoFocus,
}: ApiKeySuccessDialogProps) => {
  const { t, i18n } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const copySession = useMemo(() => ({ open, apiKey }), [open, apiKey]);
  const activeCopySession = useRef(copySession);
  useLayoutEffect(() => { activeCopySession.current = copySession; }, [copySession]);
  const keyValue = useMemo(
    () => apiKey?.secret_key ?? apiKey?.key ?? "",
    [apiKey],
  );

  const handleCopyKey = useCallback(async () => {
    if (!keyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(keyValue);
      if (mounted.current && activeCopySession.current === copySession && open) toast.success(t("apiKeyDialog.apiKeyCopied"));
    } catch {
      if (mounted.current && activeCopySession.current === copySession && open) toast.error(t("apiKeys.copyFailed"));
    }
  }, [copySession, keyValue, open, t]);

  const handleDownload = useCallback(() => {
    if (!apiKey || !keyValue) {
      return;
    }

    const content = `${t("apiKeyDialog.txtDetailsTitle")}\n===============\n${t("apiKeyDialog.keyName")}: ${apiKey.name}\n${t("apiKeyDialog.apiKey")}: ${keyValue}\n${t("apiKeys.expires")}: ${apiKey.expires_at ? new Date(apiKey.expires_at).toLocaleString() : t("common.never")}\n${t("apiKeyDialog.timestamp")}: ${new Date().toISOString()}\n\n${note}`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${apiKey.name}-api-key.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [apiKey, keyValue, note, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir={i18n.dir()}
        className="flex h-[275px] max-h-[calc(100dvh-2rem)] w-[640px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-[10px] border-border bg-card p-0 sm:max-w-[640px]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          closeButtonRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (onCloseAutoFocus) { onCloseAutoFocus(event); return; }
          if (!returnFocusRef) return;
          event.preventDefault();
          const target = returnFocusRef.current;
          if (!mounted.current || !target?.isConnected || target.disabled ||
            target.closest('[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]') ||
            document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
          for (let node: HTMLElement | null = target; node; node = node.parentElement) {
            const style = getComputedStyle(node);
            if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return;
          }
          target.focus();
        }}
      >
        <DialogHeader className="min-h-14 shrink-0 justify-center px-4 py-0 pe-12 text-start">
          <DialogTitle className="text-xl font-semibold leading-6 tracking-[-0.4px] text-card-foreground">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {note}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium leading-[18px] text-secondary-foreground">
              {t("apiKeyDialog.keyName")}
            </span>
            <p className="min-h-[21px] break-words text-sm font-medium leading-[21px] text-foreground">
              {apiKey?.name}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <span
              id="generated-api-key-label"
              className="text-xs font-medium leading-[18px] text-secondary-foreground"
            >
              {t("apiKeyDialog.apiKey")}
            </span>
            <div
              dir="ltr"
              role="group"
              aria-labelledby="generated-api-key-label"
              className="flex min-h-9 items-center gap-2 overflow-hidden rounded-[8px] border border-border bg-input px-3 py-[7.5px] shadow-xs"
            >
              <code className="min-w-0 flex-1 truncate font-mono text-sm leading-[21px] text-foreground">
                {keyValue || t("apiKeyDialog.keyValueNotReturned")}
              </code>
              <button
                type="button"
                onClick={handleCopyKey}
                className="shrink-0 rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-input disabled:pointer-events-none disabled:opacity-40"
                aria-label={t("apiKeyDialog.copyApiKey")}
                disabled={!keyValue}
              >
                <Copy className="size-5" />
              </button>
            </div>
          </div>

          <p className="break-words text-xs font-medium leading-[18px] text-muted-foreground">
            {note}
          </p>
        </div>

        <DialogFooter className="min-h-[68px] shrink-0 flex-row flex-wrap items-center justify-end gap-2 px-4 py-4 sm:flex-row">
          <Button
            ref={closeButtonRef}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border bg-card px-4 text-foreground hover:bg-accent"
          >
            {t("common.close")}
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            className="bg-primary px-4 text-primary-foreground hover:bg-primary/90"
            disabled={!keyValue}
          >
            {t("apiKeyDialog.downloadAsTxt")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeySuccessDialog;
